import { getCookies, setCookie } from 'cookies-next';
import type { CookieStorageItems } from '@/core/models/cookie-storage';

export type StorageListener<T> = (value: T | null) => void;

const listeners = new Map<string, Set<StorageListener<any>>>();

const notifyListeners = (key: string, value: unknown) => {
  listeners.get(key)?.forEach(listener => listener(value));
};

// Util type that ensures the string type of the key corresponds with the actual type of it
type ItemTypesByString<S extends Record<string, unknown>> = {
  [K in keyof S]: S[K] extends string
    ? 'string'
    : S[K] extends number
      ? 'number'
      : S[K] extends boolean
        ? 'boolean'
        : S[K] extends object
          ? 'object'
          : never;
};

export const ItemTypes: ItemTypesByString<CookieStorageItems> = {
  environment: 'string',
  orderId: 'string',
  evaOrganizationLocale: 'string',
  evaOrganizationUnitId: 'string',
  evaAppToken: 'string',
  evaAuthenticationToken: 'string',
  adyenSession: 'object',
  accessToken: 'string',
  refreshToken: 'string',
  hasAuthSession: 'boolean',
  cartItemsCount: 'number',
};

const serialize = (value: unknown) => {
  if (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  ) {
    return String(value);
  }
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

const deserialize = <T extends keyof CookieStorageItems>(
  key: T,
  value: string,
): CookieStorageItems[T] | null => {
  const type = ItemTypes[key];
  if (type === 'string') {
    return String(value) as CookieStorageItems[T];
  }
  if (type === 'number') {
    return Number(value) as unknown as CookieStorageItems[T];
  }
  if (type === 'boolean') {
    return Boolean(value) as unknown as CookieStorageItems[T];
  }
  if (type === 'object') {
    try {
      return JSON.parse(value) as CookieStorageItems[T];
    } catch {
      return value as CookieStorageItems[T];
    }
  }
  return null;
};

export const ClientCookies = {
  get: async <K extends keyof CookieStorageItems>(
    key: K,
  ): Promise<CookieStorageItems[K] | null> => {
    const stringValue = (await getCookies())?.[key] ?? null;
    if (stringValue === null) return null;

    return deserialize(key, stringValue);
  },

  set: async <K extends keyof CookieStorageItems>(
    key: K,
    value: CookieStorageItems[K],
  ) => {
    const stringValue = serialize(value);
    await setCookie(key, stringValue);
    notifyListeners(key, value);
  },

  remove: async <K extends keyof CookieStorageItems>(key: K) => {
    await setCookie(key, '', { expires: new Date(0) });
    notifyListeners(key, null);
  },

  clear: async () => {
    const cookies = await getCookies();
    for (const key of Object.keys(cookies ?? {})) {
      // eslint-disable-next-line no-await-in-loop
      await ClientCookies.remove(key as keyof CookieStorageItems);
      notifyListeners(key, null);
    }
  },

  subscribe: <K extends keyof CookieStorageItems>(
    key: K,
    listener: StorageListener<CookieStorageItems[K]>,
  ) => {
    if (!listeners.has(key)) {
      listeners.set(key, new Set());
    }
    listeners.get(key)?.add(listener);

    return () => {
      listeners.get(key)?.delete(listener);
      if (listeners.get(key)?.size === 0) {
        listeners.delete(key);
      }
    };
  },
};

const ServerCookies = {
  get: async <K extends keyof CookieStorageItems>(
    key: K,
  ): Promise<CookieStorageItems[K] | null> => {
    const { cookies } = await import('next/headers');

    const stringValue = (await cookies()).get(key)?.value ?? null;
    if (stringValue === null) return null;

    return deserialize(key, stringValue);
  },

  set: async <K extends keyof CookieStorageItems>(
    key: K,
    value: CookieStorageItems[K],
  ) => {
    const { cookies } = await import('next/headers');

    const stringValue = serialize(value);
    (await cookies()).set(key, stringValue);
  },

  remove: async <K extends keyof CookieStorageItems>(key: K) => {
    const { cookies } = await import('next/headers');
    (await cookies()).delete(key);
  },

  clear: async () => {
    const { cookies } = await import('next/headers');
    const cookiesMap = await cookies();
    for (const cookie of cookiesMap.getAll()) {
      cookiesMap.delete(cookie.name);
    }
  },
};

const Storage = typeof window === 'undefined' ? ServerCookies : ClientCookies;

export default Storage;
